import {
  AccountType,
  AddOnRateType,
  AddOnType,
  deleteV1CompaniesCompanyIdAutoAddOnAssignmentsId,
  deleteV1CompaniesCompanyIdSiteRateSchedulesId,
  patchV1CompaniesCompanyIdAutoAddOnAssignmentsId,
  patchV1CompaniesCompanyIdSiteRateSchedulesId,
  postV1CompaniesCompanyIdOrdersOrderIdSiteRateSchedules,
  postV1CompaniesCompanyIdProjectsProjectIdSiteRateSchedules,
  postV1CompaniesCompanyIdSiteRateSchedulesSiteRateScheduleIdAutoAddOnAssignments,
  SiteRateSchedule_Update,
} from '@treadinc/horizon-api-spec';
import { t } from 'i18next';
import { useCallback } from 'react';

import { AnalyticsController } from '~hooks/useAnalytics/AnalyticsController';
import { AddOnAssignmentSchema } from '~pages/Sales/Orders/NewOrderForm/components/AutomaticAddOns';
import { SiteRateScheduleOperations } from '~pages/Sales/Projects/components/SiteRatesForm';
import connection from '~services/connectionModule';
import { useStores } from '~store';
import { extractAxiosErrorInfo } from '~utils/extractAxiosError';
import { getEffectiveUserCompanyId } from '~utils/user/user-utils';

import { SiteRateSchedule } from './models';
import {
  SiteRateAddOnAccount,
  SiteRateAddOnOpResult,
  SiteRateCreateOpResult,
  SiteRateDeleteOpResult,
  SiteRateScheduleSaveResult,
  SiteRateUpdateOpResult,
} from './siteRateScheduleSaveResult';

type SiteRateScheduleOperationsTarget =
  | { type: 'project'; companyId?: string; projectId: string }
  | { type: 'order'; companyId: string; orderId: string };

interface AddOnSubBatch {
  account: SiteRateAddOnAccount;
  formIndices: number[];
  items: AddOnAssignmentSchema[];
}

const buildAddOnBatches = (
  rate: SiteRateScheduleOperations['toCreate'][number],
  formIndicesOverride?: {
    customer: number[];
    vendor: number[];
    driver: number[];
  },
): AddOnSubBatch[] => {
  const batches: AddOnSubBatch[] = [];
  const customerItems = rate.customerAddOns ?? [];
  const vendorItems = rate.vendorAddOns ?? [];
  const driverItems = rate.driverAddOns ?? [];

  if (customerItems.length) {
    batches.push({
      account: 'customer',
      items: customerItems,
      formIndices: formIndicesOverride?.customer ?? customerItems.map((_, i) => i),
    });
  }
  if (vendorItems.length) {
    batches.push({
      account: 'vendor',
      items: vendorItems,
      formIndices: formIndicesOverride?.vendor ?? vendorItems.map((_, i) => i),
    });
  }
  if (driverItems.length) {
    batches.push({
      account: 'driver',
      items: driverItems,
      formIndices: formIndicesOverride?.driver ?? driverItems.map((_, i) => i),
    });
  }
  return batches;
};

// Process a single add-on assignment and return a result tagged
// with its kind + form index. Wraps each request individually so a failure
// on one add-on never short-circuits the rest of the batch.
const runAddOnOp = async (
  companyId: string,
  siteRateScheduleId: string,
  addOn: AddOnAssignmentSchema,
  account: SiteRateAddOnAccount,
  formIndex: number,
): Promise<SiteRateAddOnOpResult> => {
  const apiAccountType: AccountType | undefined =
    account === 'customer'
      ? AccountType.CUSTOMER
      : account === 'vendor'
        ? AccountType.VENDOR
        : undefined;

  if (addOn.isDeleted && !addOn.isNew) {
    try {
      await deleteV1CompaniesCompanyIdAutoAddOnAssignmentsId({
        path: { 'company-id': companyId, id: addOn?.addOn?.id ?? '' },
      });
      return { account, index: formIndex, op: 'delete' };
    } catch (err) {
      const info = extractAxiosErrorInfo(
        err,
        t('error_messages.site_rate_schedules.failed_to_process'),
      );
      return {
        account,
        index: formIndex,
        op: 'delete',
        error: info.message,
        errorStatus: info.status,
      };
    }
  }

  if (!addOn.isDeleted && !addOn.isNew) {
    try {
      await patchV1CompaniesCompanyIdAutoAddOnAssignmentsId({
        path: { 'company-id': companyId, id: addOn?.addOn?.id ?? '' },
        body: {
          name: addOn?.addOn?.name ?? '',
          quantity: addOn?.addOn?.quantity ?? 0,
          revenue_rate_value:
            addOn?.addOn?.rateType === AddOnRateType.RATE_FOR_EACH ||
            addOn?.addOn?.rateType === AddOnRateType.PER_LOAD
              ? addOn?.addOn?.rateValue?.toString()
              : undefined,
          percentage:
            addOn?.addOn?.rateType === AddOnRateType.RATE_PERCENT_OF_TOTAL
              ? addOn?.addOn?.rateValue
              : undefined,
          add_on_rate_type: addOn?.addOn?.rateType as AddOnRateType,
          add_on_types: (addOn?.addOn?.addOnTypes?.map(
            (addOnType: { id: string; name: string }) => addOnType.id,
          ) ?? []) as AddOnType[],
          cost_rate_value: addOn?.addOn?.costRateValue
            ? String(addOn?.addOn?.costRateValue)
            : undefined,
          total_installments: addOn?.addOn?.totalInstallments,
        },
      });
      return { account, index: formIndex, op: 'update' };
    } catch (err) {
      const info = extractAxiosErrorInfo(
        err,
        t('error_messages.site_rate_schedules.failed_to_process'),
      );
      return {
        account,
        index: formIndex,
        op: 'update',
        error: info.message,
        errorStatus: info.status,
      };
    }
  }

  if (!addOn.isDeleted && addOn.isNew) {
    try {
      const response =
        await postV1CompaniesCompanyIdSiteRateSchedulesSiteRateScheduleIdAutoAddOnAssignments(
          {
            path: {
              'company-id': companyId,
              'site-rate-schedule-id': siteRateScheduleId,
            },
            body: {
              account_type: apiAccountType,
              name: addOn?.addOn?.name ?? '',
              quantity: addOn?.addOn?.quantity ?? 0,
              revenue_rate_value:
                addOn?.addOn?.rateType === AddOnRateType.RATE_FOR_EACH ||
                addOn?.addOn?.rateType === AddOnRateType.PER_LOAD
                  ? addOn?.addOn?.rateValue
                  : undefined,
              percentage:
                addOn?.addOn?.rateType === AddOnRateType.RATE_PERCENT_OF_TOTAL
                  ? addOn?.addOn?.rateValue
                  : undefined,
              add_on_rate_type: addOn?.addOn?.rateType as AddOnRateType,
              external_id: addOn?.addOn?.externalId ?? '',
              accounting_id: addOn?.addOn?.accountingId ?? '',
              add_on_id: addOn?.addOn?.addOnId ?? '',
              add_on_types: (addOn?.addOn?.addOnTypes?.map(
                (addOnType: { id: string; name: string }) => addOnType.id,
              ) ?? []) as AddOnType[],
              total_installments: addOn?.addOn?.totalInstallments ?? 0,
              cost_rate_value: addOn?.addOn?.costRateValue ?? undefined,
              original_auto_add_on_assignment_id:
                addOn?.addOn?.originalAutoAddOnAssignmentId ?? null,
            },
          },
        );
      const createdId = response?.data?.data?.id as string | undefined;
      return {
        account,
        index: formIndex,
        op: 'create',
        createdAssignmentId: createdId,
      };
    } catch (err) {
      const info = extractAxiosErrorInfo(
        err,
        t('error_messages.site_rate_schedules.failed_to_process'),
      );
      return {
        account,
        index: formIndex,
        op: 'create',
        error: info.message,
        errorStatus: info.status,
      };
    }
  }

  // isNew && isDeleted ⇒ no-op. The add-on never reached the server, so
  // there is nothing to undo. Treat as a settled delete.
  return { account, index: formIndex, op: 'delete' };
};

interface AddOnEntry {
  item: AddOnAssignmentSchema;
  account: SiteRateAddOnAccount;
  formIndex: number;
}

// Runs as three sequential phases: DELETE, then PATCH (update), then POST
// (create). Strict phase ordering means a cascade-replacement POST carrying
// `original_auto_add_on_assignment_id` can't race the DELETE of the row it
// references, and a POST that depends on an updated parent state sees the
// post-PATCH state on the wire. `runAddOnOp` already wraps each request in
// try/catch and returns a tagged result, so plain `Promise.all` per phase
// is sufficient — the sequential awaits are what enforce the ordering.
// Building each phase's calls only after the prior phase settles is
// required: `runAddOnOp` fires HTTP eagerly when invoked. The
// `isNew && isDeleted` case (an undo of a never-persisted add-on) is a
// synthetic no-op inside `runAddOnOp`; it's grouped with creates here
// because it has no wire effect either way.
const runRateAddOnBatches = async (
  companyId: string,
  siteRateScheduleId: string,
  batches: AddOnSubBatch[],
): Promise<SiteRateAddOnOpResult[]> => {
  const deletes: AddOnEntry[] = [];
  const patches: AddOnEntry[] = [];
  const creates: AddOnEntry[] = [];
  for (const batch of batches) {
    batch.items.forEach((item, idx) => {
      const entry: AddOnEntry = {
        item,
        account: batch.account,
        formIndex: batch.formIndices[idx],
      };
      if (item.isDeleted && !item.isNew) {
        deletes.push(entry);
      } else if (!item.isDeleted && !item.isNew) {
        patches.push(entry);
      } else {
        creates.push(entry);
      }
    });
  }

  const deleteResults = await Promise.all(
    deletes.map(({ item, account, formIndex }) =>
      runAddOnOp(companyId, siteRateScheduleId, item, account, formIndex),
    ),
  );

  const patchResults = await Promise.all(
    patches.map(({ item, account, formIndex }) =>
      runAddOnOp(companyId, siteRateScheduleId, item, account, formIndex),
    ),
  );

  const createResults = await Promise.all(
    creates.map(({ item, account, formIndex }) =>
      runAddOnOp(companyId, siteRateScheduleId, item, account, formIndex),
    ),
  );

  return [...deleteResults, ...patchResults, ...createResults];
};

export default function useSiteRateScheduleOperations() {
  const { userStore } = useStores();
  const companyId = getEffectiveUserCompanyId(userStore);

  // Resilient variant — runs each SRS create/update/delete and
  // each nested add-on request independently, collects a per-op result, and
  // never throws. Callers use the returned `SiteRateScheduleSaveResult` to
  // patch their form state for the rows that succeeded and surface inline
  // errors on the rows that failed.
  const processSiteRateScheduleOperationsResilient = useCallback(
    async (
      target: SiteRateScheduleOperationsTarget,
      operations: SiteRateScheduleOperations,
      // Which form family the submit came from — the `scope` property on the
      // analytics event. Distinct from `target.type`: the order form usually
      // writes the PROJECT's cards (the order tracks the project rate book),
      // so deriving scope from the write target would tag order-form saves
      // as `project` and break the funnel join with `rate_card_added` /
      // `rate_card_field_edited`, which carry the form scope.
      formScope: 'order' | 'project',
    ): Promise<SiteRateScheduleSaveResult> => {
      const rateCompanyId =
        target.type === 'project' ? target.companyId || companyId : companyId;
      const creates: SiteRateCreateOpResult[] = await Promise.all(
        operations.toCreate.map(async (rate) => {
          const clientKey = (rate.clientKey as string | undefined) ?? '';
          const body = SiteRateSchedule.deparseDTO(rate);
          // Selective rate apply: forward the selective-apply params onto a project-scoped card
          // CREATE so the new card's re-match cascade pins the same orders as the update path. The
          // two families are spread INDEPENDENTLY, mirroring the update builder below:
          //   - `skipped_order_ids`/`from`/`to` (project dialog / toggle ON): the merged
          //     impact dialog's skip selection + date range. Present only when the dialog ran.
          //   - `origin_order_id` (order-origin edit): the order the edit came from, so
          //     the BE excludes it from the pin set for the newly-created card too. Present on BOTH
          //     order-origin toggle states — a mixed save (existing-card update that cascades + a new
          //     card riding along) stamps `operations.originOrderId`, and without it the BE OFF-branch
          //     would pin the origin order and the new card wouldn't apply to the order the edit came
          //     from (AC: the origin always receives its own edit). BE consumes both on the create
          //     path (SiteRateScheduleService::Create). Cast mirrors the update PATCH
          //     path (the generated create body type doesn't declare the selective-apply fields yet).
          const createCarriesSelectiveApplyParams =
            operations.skippedOrderIds !== undefined ||
            operations.originOrderId !== undefined ||
            operations.applyRateChangesToExistingOrders !== undefined;
          const createBody =
            target.type === 'project' && createCarriesSelectiveApplyParams
              ? ({
                  ...body,
                  ...(operations.skippedOrderIds !== undefined
                    ? { skipped_order_ids: operations.skippedOrderIds }
                    : {}),
                  ...(operations.originOrderId !== undefined
                    ? { origin_order_id: operations.originOrderId }
                    : {}),
                  // Per-request override. `!== undefined` (not truthiness) so a
                  // `false` (only-origin-changes) intent is transmitted rather than silently dropped.
                  ...(operations.applyRateChangesToExistingOrders !== undefined
                    ? {
                        apply_rate_changes_to_existing_orders:
                          operations.applyRateChangesToExistingOrders,
                      }
                    : {}),
                  ...(operations.from ? { from: operations.from } : {}),
                  ...(operations.to ? { to: operations.to } : {}),
                } as typeof body)
              : body;
          let serverId: string | undefined;
          let rateError: string | undefined;
          let rateErrorStatus: number | undefined;

          try {
            const response =
              target.type === 'project'
                ? await postV1CompaniesCompanyIdProjectsProjectIdSiteRateSchedules({
                    path: {
                      'company-id': rateCompanyId,
                      'project-id': target.projectId,
                    },
                    body: createBody,
                  })
                : await postV1CompaniesCompanyIdOrdersOrderIdSiteRateSchedules({
                    path: {
                      'company-id': target.companyId,
                      'order-id': target.orderId,
                    },
                    body: createBody,
                  });
            serverId = response?.data?.data?.id as string | undefined;
          } catch (err) {
            const info = extractAxiosErrorInfo(
              err,
              t('error_messages.site_rate_schedules.failed_to_process'),
            );
            rateError = info.message;
            rateErrorStatus = info.status;
          }

          // No SRS, no add-on POSTs to attempt.
          if (!serverId) {
            return {
              kind: 'create',
              clientKey,
              serverId,
              rateError,
              rateErrorStatus,
              addOns: [],
            };
          }

          const batches = buildAddOnBatches(rate);
          const addOns = await runRateAddOnBatches(rateCompanyId, serverId, batches);
          return {
            kind: 'create',
            clientKey,
            serverId,
            rateError,
            rateErrorStatus,
            addOns,
          };
        }),
      );

      const updates: SiteRateUpdateOpResult[] = await Promise.all(
        operations.toUpdate.map(async ({ rate, body, dirtyAddOnFormIndices }) => {
          const clientKey = (rate.clientKey as string | undefined) ?? '';
          const rateId = String(rate.id ?? '');
          // Selective rate apply: on a project-scoped rate edit that actually PATCHes, forward the
          // selective-apply params so the BE pins the right orders. Only when a real rate PATCH is
          // happening (non-empty body) — never manufacture a PATCH just to carry a param. The three
          // families are spread INDEPENDENTLY:
          //   - `skipped_order_ids`/`from`/`to` (project dialog / toggle ON): the orders
          //     the user unchecked + the date range. Present only on the toggle-ON path.
          //   - `origin_order_id` (order-origin edit): the order the edit came from, so the
          //     BE excludes it from the pin set. Present on BOTH order-origin toggle states — the
          //     toggle-OFF path has NO `skipped_order_ids` but still MUST send `origin_order_id`, or
          //     the BE OFF-branch would pin the origin order and the edit wouldn't apply to it.
          const carriesSelectiveApplyParams =
            operations.skippedOrderIds !== undefined ||
            operations.originOrderId !== undefined ||
            operations.applyRateChangesToExistingOrders !== undefined;
          const patchBody =
            target.type === 'project' &&
            carriesSelectiveApplyParams &&
            Object.keys(body).length > 0
              ? {
                  ...body,
                  ...(operations.skippedOrderIds !== undefined
                    ? { skipped_order_ids: operations.skippedOrderIds }
                    : {}),
                  ...(operations.originOrderId !== undefined
                    ? { origin_order_id: operations.originOrderId }
                    : {}),
                  // Per-request override. `!== undefined` (not truthiness) so a
                  // `false` (only-origin-changes) intent is transmitted rather than silently dropped.
                  ...(operations.applyRateChangesToExistingOrders !== undefined
                    ? {
                        apply_rate_changes_to_existing_orders:
                          operations.applyRateChangesToExistingOrders,
                      }
                    : {}),
                  // Scope the cascade by date range; BE pins out-of-range orders.
                  ...(operations.from ? { from: operations.from } : {}),
                  ...(operations.to ? { to: operations.to } : {}),
                }
              : body;
          const attemptedRatePatch = Object.keys(patchBody).length > 0;
          let rateError: string | undefined;
          let rateErrorStatus: number | undefined;

          if (attemptedRatePatch) {
            try {
              await patchV1CompaniesCompanyIdSiteRateSchedulesId({
                path: { 'company-id': rateCompanyId, id: rateId },
                body: patchBody as SiteRateSchedule_Update,
              });
            } catch (err) {
              const info = extractAxiosErrorInfo(
                err,
                t('error_messages.site_rate_schedules.failed_to_process'),
              );
              rateError = info.message;
              rateErrorStatus = info.status;
            }
          }

          const batches = buildAddOnBatches(rate, dirtyAddOnFormIndices);
          const addOns = await runRateAddOnBatches(rateCompanyId, rateId, batches);

          return {
            kind: 'update',
            clientKey,
            rateId,
            attemptedRatePatch,
            rateError,
            rateErrorStatus,
            addOns,
          };
        }),
      );

      const deletes: SiteRateDeleteOpResult[] = await Promise.all(
        operations.toDelete.map(async (rate) => {
          const clientKey = (rate.clientKey as string | undefined) ?? '';
          const rateId = String(rate.id ?? '');
          // Selective rate apply: forward the merged save-time impact dialog's skip
          // selection onto a project-scoped card DELETE cascade. With the toggle ON a delete soft-deletes
          // and its row flows into `toDelete`, previewed by the merged dialog (FreightRates keys the
          // preview off `deleteIds`); the user's unchecks land in `operations.skippedOrderIds`. Without
          // this the delete cascade re-rated EVERY reachable order regardless of the selection. The BE
          // consumes skipped_order_ids/origin_order_id/from/to on DELETE; these
          // ride the QUERY string. `apply_rate_changes_to_existing_orders` is NOT declared on
          // -Delete, so it is deliberately omitted (would 422).
          const deleteCarriesSelectiveApplyParams =
            target.type === 'project' &&
            (operations.skippedOrderIds !== undefined ||
              operations.originOrderId !== undefined);
          const deleteArgs = deleteCarriesSelectiveApplyParams
            ? {
                path: { 'company-id': rateCompanyId, id: rateId },
                query: {
                  ...(operations.skippedOrderIds !== undefined
                    ? { skipped_order_ids: operations.skippedOrderIds }
                    : {}),
                  ...(operations.originOrderId !== undefined
                    ? { origin_order_id: operations.originOrderId }
                    : {}),
                  ...(operations.from ? { from: operations.from } : {}),
                  ...(operations.to ? { to: operations.to } : {}),
                },
              }
            : {
                path: { 'company-id': rateCompanyId, id: rateId },
              };
          try {
            await deleteV1CompaniesCompanyIdSiteRateSchedulesId(deleteArgs);
            return { kind: 'delete', clientKey, rateId };
          } catch (err) {
            const info = extractAxiosErrorInfo(
              err,
              t('error_messages.site_rate_schedules.failed_to_process'),
            );
            return {
              kind: 'delete',
              clientKey,
              rateId,
              rateError: info.message,
              rateErrorStatus: info.status,
            };
          }
        }),
      );

      // Analytics lives on this shared seam (not in the forms) so rate-card
      // saves from every host — Create/Update Project, Create/Update/Copy &
      // Edit Order — are counted once, by the same event.
      //
      // An update whose sparse PATCH body was empty sent no rate request
      // (`attemptedRatePatch: false`) — it only counts as an operation when
      // it carried add-on work, and never as a rate update.
      const attemptedAnyRateOperation =
        creates.length > 0 ||
        deletes.length > 0 ||
        updates.some((u) => u.attemptedRatePatch || u.addOns.length > 0);
      if (attemptedAnyRateOperation) {
        const addOnErrors = [...creates, ...updates]
          .flatMap((op) => op.addOns)
          .filter((a) => Boolean(a.error));
        // Why (not just how-bad) for the outcome event: the first failure's
        // HTTP status distinguishes user-fixable 422s from server 5xx.
        const firstRateErrorStatus = [...creates, ...updates, ...deletes].find(
          (op) => op.rateErrorStatus !== undefined,
        )?.rateErrorStatus;
        AnalyticsController.track('rate_cards_submitted', {
          scope: formScope,
          write_target: target.type,
          target_id: target.type === 'project' ? target.projectId : target.orderId,
          // A card counts as created when its SRS POST succeeded, even if
          // nested add-ons failed — the card IS persisted, and on retry the
          // row flips to an update, so excluding it would undercount creates
          // permanently. Add-on failures surface via add_on_failed_count.
          created_count: creates.filter((c) => Boolean(c.serverId)).length,
          create_failed_count: creates.filter((c) => !c.serverId).length,
          updated_count: updates.filter((u) => u.attemptedRatePatch && !u.rateError)
            .length,
          update_failed_count: updates.filter(
            (u) => u.attemptedRatePatch && Boolean(u.rateError),
          ).length,
          deleted_count: deletes.filter((d) => !d.rateError).length,
          delete_failed_count: deletes.filter((d) => Boolean(d.rateError)).length,
          add_on_failed_count: addOnErrors.length,
          rate_error_status: firstRateErrorStatus,
        });
      }

      return { creates, updates, deletes };
    },
    [companyId],
  );

  // Legacy variant — kept identical in behaviour for the non-resilient
  // callers (currently the order form). Wraps the resilient variant and
  // surfaces the first failure as a toast via `connection.handleRequestError`.
  const processSiteRateScheduleOperations = useCallback(
    async (
      target: SiteRateScheduleOperationsTarget,
      operations: SiteRateScheduleOperations,
      formScope: 'order' | 'project',
    ) => {
      const result = await processSiteRateScheduleOperationsResilient(
        target,
        operations,
        formScope,
      );

      const firstError =
        result.creates.find((c) => c.rateError)?.rateError ??
        result.creates.flatMap((c) => c.addOns).find((a) => a.error)?.error ??
        result.updates.find((u) => u.rateError)?.rateError ??
        result.updates.flatMap((u) => u.addOns).find((a) => a.error)?.error ??
        result.deletes.find((d) => d.rateError)?.rateError;

      if (firstError) {
        try {
          connection.handleRequestError(
            new Error(firstError),
            t('error_messages.site_rate_schedules.failed_to_process'),
          );
        } catch {
          // handleRequestError always throws; the toast is the side effect we want.
        }
      }
    },
    [processSiteRateScheduleOperationsResilient],
  );

  return {
    processSiteRateScheduleOperations,
    processSiteRateScheduleOperationsResilient,
  };
}
